/**
 * Axios API client with:
 * - JWT Bearer token injection
 * - Auto token refresh on 401
 * - Request/response logging
 * - Consistent error handling
 */
import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@store/authStore";
import { isSignOutInProgress, signOut } from "@utils/signOut";

const BASE_URL = import.meta.env.VITE_API_URL || (
  typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? `${window.location.protocol}//${window.location.host}/api/v1`
    : "http://localhost:8000/api/v1"
);

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }> = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

export function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: BASE_URL,
    timeout: 30000,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  // ─── Request: inject Bearer token ────────────────────────────────────────
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = useAuthStore.getState().accessToken;
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // ─── Response: handle 401 + token refresh ────────────────────────────────
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
      const url = String(originalRequest?.url || "");

      // Never refresh/retry during logout — prevents infinite loops
      if (url.includes("/auth/logout") || isSignOutInProgress()) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            if (originalRequest.headers) {
              (originalRequest.headers as Record<string, string>).Authorization = `Bearer ${token}`;
            }
            return client(originalRequest);
          });
        }
        originalRequest._retry = true;
        isRefreshing = true;
        const { refreshToken, setTokens } = useAuthStore.getState();
        if (!refreshToken) {
          await signOut();
          return Promise.reject(error);
        }
        try {
          const res = await axios.post(`${BASE_URL}/auth/token/refresh/`, { refresh: refreshToken });
          const newAccess = res.data.access;
          setTokens(newAccess, refreshToken);
          processQueue(null, newAccess);
          if (originalRequest.headers) {
            (originalRequest.headers as Record<string, string>).Authorization = `Bearer ${newAccess}`;
          }
          return client(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          await signOut();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
}

export const apiClient = createApiClient();
